import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Star,
  Check,
  X,
  Play,
  Trophy,
  Sparkles,
  TrendingUp,
  ShieldCheck,
  Lock,
  Scale,
  BookOpenCheck,
  Tag,
} from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import BrandLogo from "@/components/BrandLogo";

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  React.useEffect(() => {
    if (user) navigate("/app/dashboard", { replace: true });
  }, [user, navigate]);

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="ambient-bg" />

      {/* Nav, unchanged */}
      <header className="relative z-10 px-8 py-6 flex items-center justify-between">
        <BrandLogo variant="lockup" iconSize={36} to="/" testid="landing-brand" />
        <div className="flex items-center gap-3">
          <Link
            to="/login"
            data-testid="landing-login-link"
            className="text-sm font-medium text-zinc-400 hover:text-zinc-100 transition-colors px-3 py-1.5"
          >
            Sign In
          </Link>
          <Link
            to="/register"
            data-testid="landing-register-link"
            className="text-sm font-medium bg-zinc-50 text-zinc-950 hover:bg-white transition-colors rounded-lg px-4 py-2"
          >
            Get Started
          </Link>
        </div>
      </header>

      {/* Hero, refined messaging + Interview Readiness preview */}
      <section className="relative z-10 px-8 pt-16 pb-24 max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-14 items-center">
          {/* Left, refined copy */}
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-amber-400 border border-amber-500/20 bg-amber-500/5 px-3 py-1.5 rounded-full mb-8">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-400 pulse-dot" />
              The AI Interview Readiness Platform
            </div>
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight text-zinc-50 leading-[1.05] max-w-2xl">
              Prepare like it&apos;s
              <br className="hidden md:block" />{" "}
              the real interview.
            </h1>
            <p className="mt-6 text-lg text-zinc-400 max-w-xl leading-relaxed">
              Intuiterview analyzes your resume and target job to create personalized interview
              questions, AI coaching, and an evidence-based Interview Readiness Score, so you
              know where you stand before the real interview.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link
                to="/register"
                data-testid="landing-hero-cta"
                className="group inline-flex items-center gap-2 bg-amber-500 text-amber-950 hover:bg-amber-400 font-semibold rounded-lg px-5 py-3 shadow-[0_0_25px_rgba(245,158,11,0.25)] transition-all"
              >
                Start Preparing Free
                <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link
                to="#how-it-works"
                data-testid="landing-hero-secondary"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="inline-flex items-center gap-2 border border-zinc-800 text-zinc-200 hover:bg-zinc-900 font-medium rounded-lg px-5 py-3 transition-colors"
              >
                <Play className="h-3.5 w-3.5" /> See How It Works
              </Link>
            </div>
          </div>

          {/* Right, Interview Readiness outcome preview */}
          <HeroReadinessPreview />
        </div>

        {/* Trust strip, new, restrained */}
        <div
          data-testid="landing-trust"
          className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs text-zinc-500"
        >
          <div className="inline-flex items-center gap-1.5">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
            ))}
          </div>
          <span className="text-zinc-400">
            Built for professionals preparing for their next opportunity.
          </span>
          <span className="hidden md:inline text-zinc-700">·</span>
          <span className="inline-flex items-center gap-1.5">
            <Check className="h-3.5 w-3.5 text-emerald-400" /> Real ATS scoring
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Check className="h-3.5 w-3.5 text-emerald-400" /> Tailored cover letter
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Check className="h-3.5 w-3.5 text-emerald-400" /> Live coaching feedback
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Check className="h-3.5 w-3.5 text-emerald-400" /> Printable Interview Day Brief
          </span>
        </div>
      </section>

      {/* Interview Readiness centerpiece */}
      <section
        data-testid="landing-readiness-showcase"
        className="relative z-10 px-8 py-20 max-w-6xl mx-auto"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-[0.24em] text-amber-400 mb-4">
              your interview readiness
            </div>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-zinc-50 leading-[1.05]">
              See your readiness <br />
              <span className="text-zinc-500">before </span>the interview.
            </h2>
            <p className="mt-5 text-zinc-400 leading-relaxed max-w-md">
              We don&apos;t just help you practice. We tell you exactly how ready you are
              and what to focus on next. Every session updates your score, sharpens your stories,
              and closes the last mile of preparation.
            </p>
            <ul className="mt-6 space-y-2.5 text-sm text-zinc-300">
              <li className="flex items-start gap-2.5">
                <Check className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                Interview Readiness Score with projected next step
              </li>
              <li className="flex items-start gap-2.5">
                <Check className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                Competency breakdown: strengths and next focus
              </li>
              <li className="flex items-start gap-2.5">
                <Check className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                Recruiter-perspective coaching on every answer
              </li>
            </ul>
          </div>

          {/* Mock readiness card, decorative preview */}
          <div className="relative">
            <div className="absolute -inset-6 rounded-3xl bg-amber-500/10 blur-3xl pointer-events-none" />
            <div className="relative rounded-2xl border border-zinc-800/80 bg-gradient-to-br from-zinc-900/80 to-zinc-950/60 p-6 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)]">
              <div className="flex items-center justify-between mb-5">
                <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">
                  interview readiness
                </div>
                <span className="text-[10px] tracking-widest uppercase text-emerald-400">
                  Nearly Ready
                </span>
              </div>
              <div className="flex items-end gap-5">
                <div className="relative" style={{ width: 132, height: 132 }}>
                  <svg width="132" height="132" className="-rotate-90">
                    <circle cx="66" cy="66" r="58" stroke="#27272A" strokeWidth="8" fill="transparent" />
                    <circle
                      cx="66"
                      cy="66"
                      r="58"
                      stroke="#F59E0B"
                      strokeWidth="8"
                      strokeLinecap="round"
                      fill="transparent"
                      strokeDasharray={2 * Math.PI * 58}
                      strokeDashoffset={2 * Math.PI * 58 - 0.78 * 2 * Math.PI * 58}
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <div className="text-3xl font-bold text-zinc-50 tabular-nums">78</div>
                    <div className="text-[9px] uppercase tracking-widest text-zinc-500 mt-0.5">
                      of 100
                    </div>
                  </div>
                </div>
                <div className="flex-1 space-y-2.5 pb-2">
                  <MiniBar label="Job Match" value={92} />
                  <MiniBar label="Behavioral" value={71} />
                  <MiniBar label="Domain Knowledge" value={64} />
                </div>
              </div>
              <div className="mt-5 pt-4 border-t border-zinc-800 flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-amber-400 mb-1">
                    next step
                  </div>
                  <div className="text-sm text-zinc-100 font-medium">
                    15-min mock focused on Domain Knowledge
                  </div>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-emerald-400">
                  +6% projected
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How Intuiterview works, new */}
      <section
        id="how-it-works"
        data-testid="landing-how"
        className="relative z-10 px-8 py-20 max-w-6xl mx-auto"
      >
        <div className="text-center">
          <div className="text-[11px] uppercase tracking-[0.24em] text-amber-400 mb-3">
            how intuiterview works
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-zinc-50">
            One flow. Four steps.
          </h2>
          <p className="mt-3 text-sm text-zinc-500 max-w-xl mx-auto">
            No dashboards to configure, no dead ends. Every step compounds into interview
            readiness.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-4 gap-4 relative">
          {[
            { n: "01", title: "Upload Resume", body: "PDF, DOCX, or paste. We'll extract, structure, and store it privately." },
            { n: "02", title: "Resume & Cover Letter", body: "Match score, ATS check, missing keywords, a truthful resume rewrite, and a tailored cover letter." },
            { n: "03", title: "Interview Practice", body: "Practice Interview on webcam, then sit the Official AI Interview. Personalized coach notes after every answer." },
            { n: "04", title: "Interview Ready", body: "Readiness score, personalized coaching, and a printable brief." },
          ].map((s, i) => (
            <div
              key={s.n}
              data-testid={`landing-step-${s.n}`}
              className="relative p-5 rounded-xl border border-zinc-800/70 bg-zinc-900/40"
            >
              <div className="text-[10px] tracking-[0.28em] text-zinc-600 font-mono">STEP {s.n}</div>
              <div className="mt-2 text-lg font-semibold text-zinc-50 tracking-tight">
                {s.title}
              </div>
              <div
                className="mt-2 text-xs text-zinc-500 leading-relaxed"
              >
                {s.body}
              </div>
              {i < 3 && (
                <ArrowRight
                  className="hidden md:block absolute top-1/2 -right-3 -translate-y-1/2 h-4 w-4 text-zinc-700"
                  strokeWidth={2}
                />
              )}
            </div>
          ))}
        </div>

        <div className="mt-14 flex justify-center">
          <Link
            to="/register"
            data-testid="landing-how-cta"
            className="group inline-flex items-center gap-2 bg-amber-500 text-amber-950 hover:bg-amber-400 font-semibold rounded-lg px-5 py-3 shadow-[0_0_25px_rgba(245,158,11,0.25)] transition-all"
          >
            Prepare For My Interview
            <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>
      </section>

      {/* Why Intuiterview, comparison */}
      <section
        data-testid="landing-why"
        className="relative z-10 px-8 py-20 max-w-6xl mx-auto"
      >
        <div className="text-center">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-amber-400 border border-amber-500/20 bg-amber-500/5 px-3 py-1.5 rounded-full mb-6">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-400 pulse-dot" />
            Why Intuiterview?
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-zinc-50 leading-tight max-w-3xl mx-auto">
            Replace your entire interview
            <br />
            <span className="text-zinc-500">preparation process.</span>
          </h2>
          <p className="mt-4 text-sm text-zinc-500 max-w-xl mx-auto leading-relaxed">
            Most candidates prepare in pieces. Intuiterview brings everything together.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Most Candidates card */}
          <div
            data-testid="landing-compare-most-candidates"
            className="relative p-7 rounded-2xl border border-zinc-800/70 bg-zinc-900/30 hover:border-zinc-700 transition-all duration-300"
          >
            <div className="text-xl font-semibold text-zinc-300 tracking-tight">
              Most Candidates
            </div>
            <ul className="mt-6 space-y-3 text-sm text-zinc-500">
              {[
                "Search random interview questions",
                "Edit resumes manually",
                "Practice alone",
                "Don't know if they're ready",
                "Hope for the best",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span className="mt-0.5 h-4 w-4 rounded-full border border-zinc-700 bg-zinc-900 flex items-center justify-center flex-shrink-0">
                    <X className="h-2.5 w-2.5 text-zinc-600" strokeWidth={2.5} />
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Intuiterview card, gold accent */}
          <div
            data-testid="landing-compare-intuiterview"
            className="relative p-7 rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.06] via-zinc-900/40 to-zinc-950/60 hover:border-amber-500/50 hover:-translate-y-0.5 transition-all duration-300 shadow-[0_0_40px_-15px_rgba(245,158,11,0.35)]"
          >
            <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-amber-500/10 to-transparent pointer-events-none opacity-60" />
            <div className="relative">
              <div className="text-xl font-semibold tracking-tight">
                <span className="text-zinc-50">Offer</span>
                <span className="text-amber-400">Ready</span>
              </div>
              <ul className="mt-6 space-y-3 text-sm text-zinc-200">
                {[
                  "Predicts questions for your exact role",
                  "Aligns your resume to the job",
                  "Tailored cover letter, ready to send",
                  "Official AI Interview",
                  "Interview Readiness Score",
                  "Printable Interview Day Brief",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-0.5 h-4 w-4 rounded-full bg-amber-500/15 border border-amber-500/40 flex items-center justify-center flex-shrink-0">
                      <Check className="h-2.5 w-2.5 text-amber-400" strokeWidth={3} />
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Closing statement */}
      <section className="relative z-10 px-8 py-24 max-w-4xl mx-auto text-center">
        <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-zinc-50 leading-tight">
          Interviews shouldn&apos;t be left to chance.
        </h2>
        <p className="mt-4 text-zinc-400 max-w-2xl mx-auto leading-relaxed">
          Intuiterview is the calm, honest coach in your corner, from resume to offer.
        </p>
        <div className="mt-8">
          <Link
            to="/register"
            data-testid="landing-final-cta"
            className="inline-flex items-center gap-2 bg-amber-500 text-amber-950 hover:bg-amber-400 font-semibold rounded-lg px-5 py-3 shadow-[0_0_25px_rgba(245,158,11,0.25)] transition-all"
          >
            Start Preparing
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <footer
        data-testid="landing-footer"
        className="relative z-10 border-t border-zinc-800/60"
      >
        <div className="max-w-6xl mx-auto px-6 sm:px-8 py-14 sm:py-16">
          {/* Top block, brand + link columns */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8">
            {/* Brand block */}
            <div className="md:col-span-5 space-y-4 text-center md:text-left">
              <Link
                to="/"
                className="inline-flex items-center gap-2 group"
                data-testid="footer-brand"
              >
                <BrandLogo className="h-6 w-auto text-zinc-50 transition-colors group-hover:text-white" />
              </Link>
              <p className="text-sm text-zinc-400 leading-relaxed max-w-md mx-auto md:mx-0">
                Built for professionals who refuse to leave interviews to chance.
              </p>
            </div>

            {/* Product column */}
            <div className="md:col-span-3 space-y-4 text-center md:text-left">
              <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-medium">
                Product
              </div>
              <ul className="space-y-2.5">
                <li>
                  <Link
                    to="/pricing"
                    data-testid="footer-pricing"
                    className="group inline-flex items-center gap-2 text-sm text-zinc-100 hover:text-white transition-colors duration-200"
                  >
                    <Tag className="h-3.5 w-3.5 text-zinc-500 group-hover:text-amber-300 transition-colors duration-200" />
                    <span className="underline underline-offset-4 decoration-transparent group-hover:decoration-amber-400/60 transition-[text-decoration-color] duration-200">
                      Pricing
                    </span>
                  </Link>
                </li>
                <li>
                  <a
                    href="/#features"
                    data-testid="footer-features"
                    className="group inline-flex items-center gap-2 text-sm text-zinc-100 hover:text-white transition-colors duration-200"
                  >
                    <Sparkles className="h-3.5 w-3.5 text-zinc-500 group-hover:text-amber-300 transition-colors duration-200" />
                    <span className="underline underline-offset-4 decoration-transparent group-hover:decoration-amber-400/60 transition-[text-decoration-color] duration-200">
                      Features
                    </span>
                  </a>
                </li>
              </ul>
            </div>

            {/* Trust & Security column */}
            <div className="md:col-span-4 space-y-4 text-center md:text-left">
              <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-medium">
                Trust &amp; Security
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 gap-y-2.5 gap-x-4 justify-items-center md:justify-items-start">
                <li>
                  <Link
                    to="/trust"
                    data-testid="footer-trust"
                    className="group inline-flex items-center gap-2 text-sm text-zinc-100 hover:text-white transition-colors duration-200"
                  >
                    <ShieldCheck className="h-3.5 w-3.5 text-zinc-500 group-hover:text-amber-300 transition-colors duration-200" />
                    <span className="underline underline-offset-4 decoration-transparent group-hover:decoration-amber-400/60 transition-[text-decoration-color] duration-200">
                      Trust Center
                    </span>
                  </Link>
                </li>
                <li>
                  <Link
                    to="/trust/security"
                    data-testid="footer-security"
                    className="group inline-flex items-center gap-2 text-sm text-zinc-100 hover:text-white transition-colors duration-200"
                  >
                    <Lock className="h-3.5 w-3.5 text-zinc-500 group-hover:text-amber-300 transition-colors duration-200" />
                    <span className="underline underline-offset-4 decoration-transparent group-hover:decoration-amber-400/60 transition-[text-decoration-color] duration-200">
                      Security
                    </span>
                  </Link>
                </li>
                <li>
                  <Link
                    to="/trust/ai-transparency"
                    data-testid="footer-ai"
                    className="group inline-flex items-center gap-2 text-sm text-zinc-100 hover:text-white transition-colors duration-200"
                  >
                    <BookOpenCheck className="h-3.5 w-3.5 text-zinc-500 group-hover:text-amber-300 transition-colors duration-200" />
                    <span className="underline underline-offset-4 decoration-transparent group-hover:decoration-amber-400/60 transition-[text-decoration-color] duration-200">
                      AI Transparency
                    </span>
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom bar, copyright + legal */}
          <div className="mt-12 pt-6 border-t border-zinc-800/50 flex flex-col md:flex-row items-center justify-between gap-4 text-sm">
            <span className="text-zinc-500" data-testid="footer-copyright">
              © 2026 Intuiterview. All rights reserved.
            </span>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
              <Link
                to="/trust/privacy"
                data-testid="footer-privacy"
                className="group inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors duration-200"
              >
                <Scale className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                <span className="underline underline-offset-4 decoration-transparent group-hover:decoration-amber-400/60 transition-[text-decoration-color] duration-200">
                  Privacy Policy
                </span>
              </Link>
              <Link
                to="/trust/terms"
                data-testid="footer-terms"
                className="group inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors duration-200"
              >
                <span className="underline underline-offset-4 decoration-transparent group-hover:decoration-amber-400/60 transition-[text-decoration-color] duration-200">
                  Terms of Service
                </span>
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function MiniBar({ label, value }) {
  const color = value >= 80 ? "bg-emerald-500" : value >= 55 ? "bg-amber-500" : "bg-violet-500";
  return (
    <div>
      <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
        <span>{label}</span>
        <span className="tabular-nums text-zinc-300">{value}</span>
      </div>
      <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

/**
 * HeroReadinessPreview - the outcome-first visual for the Hero.
 * Renders a static "Interview Ready" state (91%) with competency highlights,
 * projected next-step gain, and an AI hiring recommendation. Purely visual -
 * numbers are illustrative and do not fetch from the backend.
 */
function HeroReadinessPreview() {
  const score = 91;
  const R = 62;
  const C = 2 * Math.PI * R;
  const offset = C - (score / 100) * C;
  return (
    <div className="relative" data-testid="landing-hero-readiness-preview">
      {/* Ambient glow */}
      <div className="absolute -inset-8 rounded-[2rem] bg-amber-500/[0.08] blur-3xl pointer-events-none" />
      <div className="absolute -top-4 -right-4 h-40 w-40 rounded-full bg-emerald-500/[0.05] blur-3xl pointer-events-none" />

      <div className="relative rounded-2xl border border-zinc-800/80 bg-gradient-to-br from-zinc-900/90 to-zinc-950/70 p-6 shadow-[0_25px_70px_-25px_rgba(0,0,0,0.7)] backdrop-blur">
        {/* Card header */}
        <div className="flex items-center justify-between mb-5">
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">
            interview readiness
          </div>
          <span
            data-testid="landing-hero-readiness-status"
            className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase text-emerald-300 border border-emerald-500/30 bg-emerald-500/[0.08] px-2 py-0.5 rounded-full"
          >
            <Trophy className="h-3 w-3" strokeWidth={2.5} />
            Interview Ready
          </span>
        </div>

        {/* Gauge + Competencies */}
        <div className="flex items-center gap-5">
          <div className="relative flex-shrink-0" style={{ width: 140, height: 140 }}>
            <svg width="140" height="140" className="-rotate-90">
              <circle cx="70" cy="70" r={R} stroke="#27272A" strokeWidth="9" fill="transparent" />
              <circle
                cx="70"
                cy="70"
                r={R}
                stroke="url(#hero-readiness-grad)"
                strokeWidth="9"
                strokeLinecap="round"
                fill="transparent"
                strokeDasharray={C}
                strokeDashoffset={offset}
              />
              <defs>
                <linearGradient id="hero-readiness-grad" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#10B981" />
                  <stop offset="100%" stopColor="#F59E0B" />
                </linearGradient>
              </defs>
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div
                className="text-4xl font-bold text-zinc-50 tabular-nums"
                data-testid="landing-hero-readiness-score"
              >
                {score}
                <span className="text-lg text-zinc-500 font-semibold ml-0.5">%</span>
              </div>
              <div className="text-[9px] uppercase tracking-widest text-emerald-400 mt-1 font-medium">
                Interview Ready
              </div>
            </div>
          </div>

          <div className="flex-1 space-y-2.5 pb-1">
            <div className="text-[9px] uppercase tracking-[0.28em] text-zinc-500 mb-2 font-medium">
              Competency Highlights
            </div>
            <MiniBar label="Leadership" value={93} />
            <MiniBar label="Communication" value={89} />
            <MiniBar label="Problem Solving" value={87} />
          </div>
        </div>

        {/* Projected + AI recommendation */}
        <div className="mt-5 pt-4 border-t border-zinc-800 grid grid-cols-2 gap-4">
          <div>
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-amber-400 mb-1.5">
              <TrendingUp className="h-3 w-3" strokeWidth={2.5} />
              Projected
            </div>
            <div
              className="text-sm text-zinc-100 font-medium leading-snug"
              data-testid="landing-hero-readiness-projected"
            >
              <span className="text-emerald-300 font-semibold">94%</span> after one more practice
            </div>
          </div>
          <div>
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-amber-400 mb-1.5">
              <Sparkles className="h-3 w-3" strokeWidth={2.5} />
              AI Recommendation
            </div>
            <div
              className="text-sm text-zinc-100 font-medium leading-snug"
              data-testid="landing-hero-readiness-recommendation"
            >
              Proceed to Final Interview
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
